import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Download, Eye, ArrowLeft, FileText, LogOut,
         FileDown, Archive, ChevronRight } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";

export function BackofficeListPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({
    status: "", priority: "", entity_type: "", has_pending_response: "",
  });

  useEffect(() => {
    const q = {
      status: filters.status,
      priority: filters.priority,
      entity_type: filters.entity_type,
      has_pending_response:
        filters.has_pending_response === "yes" ? true :
        filters.has_pending_response === "no"  ? false : undefined,
    };
    api.backofficeList(q).then(setRows).catch(() => setRows([]));
  }, [filters.status, filters.priority, filters.entity_type,
      filters.has_pending_response]);

  const toggleFilter = (key, value) =>
    setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900">
            <Building2 size={22} className="text-[#C5283F]" />
            <span className="font-display text-xl font-semibold">Infin · Back office</span>
          </Link>
          <div className="flex items-center gap-3 text-xs">
            <Link to="/admin/metrics" className="text-slate-600 hover:text-slate-900"
                  data-testid="nav-metrics">Dashboard</Link>
            <Link to="/admin/users" className="text-slate-600 hover:text-slate-900"
                  data-testid="nav-users">Users</Link>
            <Link to="/admin/escalations" className="text-slate-600 hover:text-slate-900"
                  data-testid="nav-escalations">Escalations</Link>
            <Link to="/admin/kb" className="text-slate-600 hover:text-slate-900"
                  data-testid="nav-kb">KB</Link>
            <span className="text-slate-500">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }}
                    data-testid="admin-logout-btn"
                    className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-2">Completed cases</h1>
        <p className="text-slate-500 mb-6">Cases that have completed intake and are ready for filing.</p>

        {/* WO 6d — server-side filter chips */}
        <div className="flex flex-wrap gap-2 mb-6 items-center text-[12px]"
              data-testid="bo-filter-bar">
          <span className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold mr-1">
            Filter:
          </span>
          {[
            ["status", "INTAKE_COMPLETE", "Intake"],
            ["status", "PAID_HANDED_OFF", "Paid"],
            ["status", "IN_FILING",       "In filing"],
            ["status", "INCORPORATED",    "Incorporated"],
            ["priority", "HIGH",          "⚠ HIGH only"],
            ["entity_type", "PRIVATE_LIMITED", "Pvt Ltd"],
            ["entity_type", "OPC",        "OPC"],
            ["has_pending_response", "yes", "Pending founder response"],
          ].map(([key, value, label]) => {
            const active = filters[key] === value;
            return (
              <button
                key={`${key}:${value}`}
                onClick={() => toggleFilter(key, value)}
                data-testid={`bo-filter-${key}-${value}`}
                className={[
                  "px-3 py-1.5 rounded-full border transition-colors",
                  active
                    ? "bg-[#C5283F] text-white border-[#C5283F]"
                    : "bg-white text-slate-700 border-slate-300 hover:border-[#C5283F]",
                ].join(" ")}
              >
                {label}
              </button>
            );
          })}
          {Object.values(filters).some((v) => v) && (
            <button onClick={() => setFilters({status:"",priority:"",entity_type:"",has_pending_response:""})}
                    data-testid="bo-filter-clear"
                    className="ml-2 text-slate-500 hover:text-[#C5283F] underline">
              Clear all
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          {rows === null && <div className="p-8 text-sm text-slate-500">Loading…</div>}
          {rows?.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm">
              No completed cases yet. Once a founder finishes intake, their case will appear here.
            </div>
          )}
          {rows && rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Priority</th>
                  <th className="text-left px-6 py-3 font-semibold">Case ID</th>
                  <th className="text-left px-6 py-3 font-semibold">Company name</th>
                  <th className="text-left px-6 py-3 font-semibold">Applicant</th>
                  <th className="text-left px-6 py-3 font-semibold">Status</th>
                  <th className="text-left px-6 py-3 font-semibold">Updated</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.case_id}
                      data-testid={`bo-row-${r.case_id}`}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${r.priority === "HIGH" ? "bg-red-50/40" : ""}`}>
                    <td className="px-6 py-3">
                      {r.priority === "HIGH" ? (
                        <span
                          data-testid={`bo-priority-high-${r.case_id}`}
                          title={`${r.high_flag_count || 0} HIGH authenticity flag(s); `
                                  + `${r.signed_off_count || 0} signed off by admin`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-red-600 text-white"
                        >
                          ⚠ High
                          {r.high_flag_count ? (
                            <span className="ml-1 opacity-90"
                                   data-testid={`bo-flagged-signed-${r.case_id}`}>
                              · {r.high_flag_count} flagged
                              {r.signed_off_count > 0 ? ` / ${r.signed_off_count} signed-off` : ""}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400">Normal</span>
                      )}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">{r.case_id}</td>
                    <td className="px-6 py-3 font-medium">{r.company_name || "—"}</td>
                    <td className="px-6 py-3 text-slate-600">{r.applicant_email}</td>
                    <td className="px-6 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 w-fit">
                          {(r.status || "").replace(/_/g, " ").toLowerCase()}
                        </span>
                        {r.needs_founder_response_review && (
                          <span
                            data-testid={`bo-needs-review-${r.case_id}`}
                            title="A founder response is waiting on this case — open it to read and action."
                            className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded text-[#7A0E1F] bg-[#FEF3F2] border border-[#C5283F]/30 w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C5283F]" />
                            Action needed
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-slate-500 text-xs">{new Date(r.updated_at).toLocaleString()}</td>
                    <td className="px-6 py-3 text-right">
                      <Link to={`/admin/cases/${r.case_id}`}
                            data-testid={`bo-open-${r.case_id}`}
                            className="text-[#C5283F] font-medium underline text-xs">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export function BackofficeCasePage() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [aadhaarOpen, setAadhaarOpen] = useState({}); // stakeholder_id -> revealed value

  useEffect(() => { api.backofficeGet(caseId).then(setCaseData).catch(() => toast.error("Case not found")); }, [caseId]);

  if (!caseData) return <div className="p-12 text-slate-500">Loading…</div>;

  const company = caseData.company || {};
  const comm = caseData.communication || {};
  const stakeholders = caseData.stakeholders || [];
  const checks = caseData.validation?.checks || [];

  async function reveal(sid) {
    try {
      const r = await api.backofficeRevealAadhaar(caseId, sid);
      setAadhaarOpen((m) => ({ ...m, [sid]: r.aadhaar_number }));
      toast.success("Decryption logged to audit trail");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 text-sm">
            <ArrowLeft size={14} /> All cases
          </Link>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div>
          <div className="text-xs text-slate-500 font-mono">{caseData.case_meta.case_id}</div>
          <h1 className="font-display text-3xl font-semibold mt-1">
            {company.name_preferences?.[0]?.proposed_name || "Untitled"}
          </h1>
          <div className="text-sm text-slate-500 mt-1">Applicant: {caseData.applicant?.email}</div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <CaseExportBtn caseId={caseId} kind="pdf" />
            <CaseExportBtn caseId={caseId} kind="zip" />
          </div>
        </div>

        <FilingProgressDriver
          caseId={caseId}
          filingProgress={caseData.filing_progress}
          status={caseData?.case_meta?.status}
          onChange={async () => {
            const fresh = await api.backofficeGet(caseId);
            setCaseData(fresh);
          }}
        />

        <Card title="Company">
          <KV label="Entity type" value={company.entity_type} />
          <KV label="Industry" value={company.industry} />
          <KV label="Business description" value={company.business_description} />
          <KV label="Main objects (draft)" value={company.main_objects_draft || "—"} />
          <KV label="Name preferences" value={
            (company.name_preferences || []).map((n) =>
              `${n.proposed_name} (${n.precheck?.status || "—"})`).join(" · ") || "—"} />
        </Card>

        <Card title="Registered office">
          {comm.registered_address?.line1 ? (
            <div className="text-sm text-slate-700">
              {comm.registered_address.line1}, {comm.registered_address.line2}<br />
              {comm.registered_address.city && <>{comm.registered_address.city}, </>}
              {comm.registered_address.state} — {comm.registered_address.pincode}
            </div>
          ) : <div className="text-sm text-slate-400 italic">Not provided</div>}
          <KV label="Premises ownership" value={comm.premises_ownership} />
          <KV label="Registered email" value={comm.registered_email} />
          <KV label="Registered mobile" value={comm.registered_mobile} />
        </Card>

        <Card title={`Stakeholders (${stakeholders.length})`}>
          {stakeholders.map((s) => {
            const name = [s.personal?.first_name, s.personal?.last_name].filter(Boolean).join(" ");
            const docs = s.documents || {};
            const authFlags = s.data_provenance?.authenticity_flags || [];
            const highFlags = authFlags.filter((f) => f.severity === "HIGH");
            const lowFlags = authFlags.filter((f) => f.severity === "LOW");
            return (
              <div key={s.stakeholder_id}
                   data-testid={`bo-stakeholder-${s.stakeholder_id}`}
                   className="border-t border-slate-100 first:border-0 pt-4 pb-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{name || "Unnamed"}</div>
                  <div className="text-xs text-slate-500">{(s.role || "").replace(/_/g, " ")} · {s.share_percent ?? "—"}%</div>
                </div>
                {authFlags.length > 0 && (
                  <div className={[
                    "mt-3 rounded-lg p-3 border text-xs",
                    highFlags.length > 0
                      ? "bg-red-50 border-red-200 text-red-900"
                      : "bg-amber-50 border-amber-200 text-amber-900",
                  ].join(" ")} data-testid={`bo-authenticity-banner-${s.stakeholder_id}`}>
                    <div className="font-semibold uppercase tracking-wider text-[10px] mb-1.5">
                      {highFlags.length > 0
                        ? `⚠ Manual verification required — ${highFlags.length} HIGH${lowFlags.length ? ` + ${lowFlags.length} LOW` : ""} authenticity flag(s)`
                        : `Authenticity notes — ${lowFlags.length} LOW flag(s)`}
                    </div>
                    <ul className="space-y-2">
                      {authFlags.map((f, i) => {
                        // WO 6c — was THIS specific flag already signed off?
                        // The detail page now ships the manual_verifications
                        // list from /backoffice/cases/{id}.
                        const signedRecord = (caseData.manual_verifications || []).find(
                          (mv) => mv.stakeholder_id === s.stakeholder_id
                                && mv.flag_name === f.flag,
                        );
                        return (
                          <li key={`${s.stakeholder_id}-${f.flag}`}
                              data-testid={`bo-flag-row-${s.stakeholder_id}-${f.flag}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <span className="font-mono mr-1.5">[{f.severity}]</span>
                                <span className="font-medium">{f.flag}</span>
                                {f.detail && <span> — {f.detail}</span>}
                              </div>
                              {f.severity === "HIGH" && !signedRecord && (
                                <ManualVerifyButton
                                  caseId={caseId}
                                  stakeholderId={s.stakeholder_id}
                                  flagName={f.flag}
                                  fileIdHint={pickFileIdForFlag(s.documents, f.flag)}
                                  onSigned={async () => {
                                    const fresh = await api.backofficeGet(caseId);
                                    setCaseData(fresh);
                                    toast.success("Sign-off recorded to audit trail");
                                  }}
                                />
                              )}
                              {f.severity === "HIGH" && signedRecord && (
                                <span
                                  data-testid={`bo-signed-${s.stakeholder_id}-${f.flag}`}
                                  className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-700 text-white"
                                  title={`Signed off by ${signedRecord.admin_email || signedRecord.admin_user_id} on ${signedRecord.recorded_at}`}
                                >
                                  ✓ Signed off
                                </span>
                              )}
                            </div>
                            {signedRecord && (
                              <div className="mt-1 ml-6 text-[11px] text-emerald-900/80 italic">
                                {signedRecord.admin_email || signedRecord.admin_user_id}
                                {" · "}
                                {(signedRecord.recorded_at || "").slice(0, 16).replace("T", " ")}
                                {" · "}
                                &ldquo;{signedRecord.reason}&rdquo;
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                  <KV label="PAN" value={s.identity?.pan} />
                  <KV label="DOB" value={s.personal?.date_of_birth} />
                  <KV label="Father's name" value={s.personal?.fathers_name} />
                  <KV label="Email" value={s.contact?.email} />
                  <KV label="Mobile" value={s.contact?.mobile} />
                  <div className="flex justify-between py-1.5 text-sm gap-3">
                    <span className="text-slate-500">Aadhaar</span>
                    <span className="text-slate-900 font-medium font-mono text-right">
                      {aadhaarOpen[s.stakeholder_id]
                        ? aadhaarOpen[s.stakeholder_id]
                        : `•••• •••• ${s.identity?.aadhaar_last4 || "----"}`}
                      <button
                        onClick={() => reveal(s.stakeholder_id)}
                        data-testid={`reveal-aadhaar-${s.stakeholder_id}`}
                        className="ml-2 text-xs text-[#C5283F] underline inline-flex items-center gap-1"
                      >
                        <Eye size={11} /> {aadhaarOpen[s.stakeholder_id] ? "Hide" : "Reveal"}
                      </button>
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {docs.pan_proof && <DownloadBtn label="PAN proof" fileId={docs.pan_proof.file_id} />}
                  {docs.aadhaar_proof && <DownloadBtn label="Aadhaar proof" fileId={docs.aadhaar_proof.file_id} />}
                  {docs.photo && <DownloadBtn label="Photo" fileId={docs.photo.file_id} />}
                  {docs.supporting_doc && <DownloadBtn label="Supporting" fileId={docs.supporting_doc.file_id} />}
                </div>
              </div>
            );
          })}
        </Card>

        <Card title="Company documents">
          {caseData.company_documents?.address_proof
            ? <DownloadBtn label="Registered-office address proof" fileId={caseData.company_documents.address_proof.file_id} />
            : caseData.company_documents?.address_proof_deferred
              ? <div className="text-sm text-amber-800">Deferred (30-day post-incorporation).</div>
              : <div className="text-sm text-slate-400 italic">Not uploaded</div>}
        </Card>

        {(caseData.validation?.validity_flags || []).length > 0 && (
          <Card title={`Validity flags — confirm with founder (${(caseData.validation.validity_flags || []).length})`}>
            <div className="text-xs text-slate-500 mb-2" data-testid="validity-flags-note">
              §3a A-with-flagging — intake was completed despite these
              checks. Resolve with the founder during filing prep.
            </div>
            <ul className="space-y-2 text-sm">
              {caseData.validation.validity_flags.map((f, i) => (
                <li key={f.rule_id || `vf-${i}`} className="rounded-md border border-[#C5283F]/30 bg-[#FEF3F2] px-3 py-2"
                    data-testid={`validity-flag-${f.rule_id}`}>
                  <div className="font-semibold text-[#7A0E1F]">{f.label || f.rule_id}</div>
                  {f.subject_ref && (
                    <div className="text-xs text-slate-600">{f.subject_ref}</div>
                  )}
                  {f.message && (
                    <div className="text-sm text-slate-700 mt-1">{f.message}</div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card title={`Validation (${checks.filter(c=>c.status==='PASS').length} of ${checks.length} passing)`}>
          <ul className="space-y-1 text-sm">
            {checks.map((c, i) => (
              <li key={c.rule_id || c.label || `check-${i}`} className={c.status === "FAIL" ? "text-red-800" : "text-slate-700"}>
                <span className="font-mono text-xs mr-2">{c.status}</span>
                <span className="font-medium">{c.label || c.rule_id}</span>
                {c.category && (
                  <span className={`ml-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                    c.category === 'presence' ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-800'
                  }`}>{c.category}</span>
                )}
                {c.message && <span className="text-slate-500"> — {c.message}</span>}
              </li>
            ))}
          </ul>
        </Card>

        {(caseData.conversation_state?.escalations || []).length > 0 && (
          <Card title="Escalations to CS/CA">
            <ul className="space-y-2 text-sm">
              {caseData.conversation_state.escalations.map((e, i) => (
                <li key={e.raised_at || e.question || `esc-${i}`} className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-amber-900">
                  <div className="font-medium">{e.question}</div>
                  <div className="text-xs text-amber-700 mt-1">{new Date(e.raised_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {caseData.handoff?.back_office_notes && (
          <Card title="Notes from Infi">
            <div className="text-sm text-slate-700 whitespace-pre-wrap">
              {caseData.handoff.back_office_notes}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-4 pb-3 border-b border-slate-100">
        {title}
      </div>
      {children}
    </div>
  );
}
function KV({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 text-sm gap-3">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-900 font-medium text-right break-words">{value || "—"}</span>
    </div>
  );
}
function DownloadBtn({ label, fileId }) {
  return (
    <a href={`${api.API}/files/${fileId}`}
       target="_blank"
       rel="noreferrer"
       data-testid={`download-${fileId}`}
       className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md"
       onClick={(e) => {
         // Use auth header by streaming through fetch then re-anchoring is overkill;
         // we rely on the cookie-less bearer flow — open a new tab won't carry the token.
         // So instead, use fetch with token and create object URL.
         e.preventDefault();
         api.default.get(`/files/${fileId}`, { responseType: "blob" })
           .then((r) => {
             const url = URL.createObjectURL(r.data);
             const a = document.createElement("a");
             a.href = url; a.download = label.replace(/\s+/g, "_");
             document.body.appendChild(a); a.click(); a.remove();
             setTimeout(() => URL.revokeObjectURL(url), 5000);
           })
           .catch(() => toast.error("Download failed"));
       }}>
      <Download size={12} /> {label}
    </a>
  );
}

// Phase 4 Fire 2 — admin filing-progress driver. Calls the existing Phase 2
// POST /api/cases/:id/filing-progress endpoint directly; no new endpoint.
const STAGE_KEYS = ["payment_received","under_review","dsc_in_progress",
  "name_filed","name_result","docs_prep","filed_mca","mca_query",
  "incorporated","handover"];
const STAGE_LABELS = {
  payment_received: "Payment received", under_review: "Document review",
  dsc_in_progress: "Digital signatures", name_filed: "Company name submitted",
  name_result: "Company name approval", docs_prep: "Incorporation documents",
  filed_mca: "Filed with the Registrar", mca_query: "MCA query",
  incorporated: "Incorporated", handover: "Handover",
};
const STATE_KEYS = ["pending","in_progress","action_needed","query_raised","done"];
const ACTION_STATES = new Set(["action_needed","query_raised"]);

const POST_PAID_STATUSES = new Set(["PAID_HANDED_OFF","IN_FILING","INCORPORATED"]);

function FilingProgressDriver({ caseId, filingProgress, status, onChange }) {
  const isPostPaid = POST_PAID_STATUSES.has(status);
  const [stage, setStage] = useState("payment_received");
  const [state, setState] = useState("done");
  const [actionMsg, setActionMsg] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const stages = filingProgress?.stages || [];
  const currentStage = filingProgress?.current_stage;

  // Spine: pre-payment cases cannot have filing progress driven. The
  // backend rejects with 409 anyway; the UI hides the controls and
  // explains why, so admins don't try.
  if (!isPostPaid) {
    return (
      <Card title="Filing progress (admin)">
        <div className="text-sm text-slate-600" data-testid="filing-driver-prepayment-notice">
          Filing hasn&apos;t started — this case hasn&apos;t been paid for yet.
          Filing progress can only be driven after payment is confirmed.
        </div>
      </Card>
    );
  }

  const submit = async () => {
    if (ACTION_STATES.has(state) && !actionMsg.trim()) {
      toast.error("Action message is required for action_needed / query_raised");
      return;
    }
    setBusy(true);
    try {
      const body = { stage, state, note: note || null };
      if (ACTION_STATES.has(state)) body.action_message = actionMsg;
      await api.setFilingProgress(caseId, body);
      toast.success(`${STAGE_LABELS[stage]} → ${state}`);
      setActionMsg(""); setNote("");
      await onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Filing progress (admin)">
      <div className="space-y-3" data-testid="filing-driver-card">
        <div className="text-xs text-slate-500">
          Current stage: <span className="font-semibold text-slate-900" data-testid="driver-current-stage">{currentStage ? (STAGE_LABELS[currentStage] || currentStage) : "—"}</span>
        </div>

        {filingProgress?.pending_founder_response && (
          <div
            data-testid="founder-pending-banner"
            className="flex items-center justify-between gap-3 rounded-md border border-[#C5283F]/30 bg-[#FEF3F2] px-3 py-2 text-xs">
            <div className="flex items-center gap-2 text-[#7A0E1F]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C5283F]" />
              <span className="font-semibold">A founder response needs your review</span>
              <span className="text-slate-600">
                · on <span className="font-medium text-slate-900">{STAGE_LABELS[filingProgress.pending_founder_response.stage] || filingProgress.pending_founder_response.stage}</span>
              </span>
            </div>
            <span className="text-[10px] text-slate-500">See stage below ↓</span>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
          {STAGE_KEYS.map((k) => {
            const s = stages.find((x) => x.key === k);
            const st = s?.state || "pending";
            const isCurrent = currentStage === k;
            // Phase 4.6 follow-up: surface founder responses on the
            // matching stage. Most-recent prominent; priors small.
            const stageResponses = (filingProgress?.founder_responses || [])
              .filter((r) => r.stage_key === k);
            const latest = stageResponses[stageResponses.length - 1] || null;
            const priors = stageResponses.slice(0, -1);
            const isPending =
              filingProgress?.pending_founder_response?.stage === k;
            return (
              <div key={k} data-testid={`driver-row-${k}`}>
                <div className="flex justify-between items-center">
                  <span className={isCurrent ? "font-semibold text-slate-900" : "text-slate-700"}>
                    <ChevronRight size={10} className="inline -translate-y-0.5 mr-1" />
                    {STAGE_LABELS[k]}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                    st === "done" ? "bg-emerald-100 text-emerald-900" :
                    ACTION_STATES.has(st) ? "bg-amber-100 text-amber-900" :
                    st === "in_progress" ? "bg-[#C5283F]/10 text-[#7A0E1F]" :
                    "bg-slate-100 text-slate-500"
                  }`}>{st}</span>
                </div>
                {latest && (
                  <div className="ml-4 mt-1 mb-2"
                       data-testid={`driver-row-${k}-founder-response`}>
                    <div className={`rounded-md p-2 text-xs ${
                      isPending ? "bg-[#FEF3F2] border border-[#C5283F]"
                                : "bg-white border border-slate-200"}`}>
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${
                        isPending ? "text-[#7A0E1F]" : "text-slate-500"}`}>
                        Founder&apos;s response {isPending ? "· unactioned" : "· actioned"}
                      </div>
                      <div className={`mt-1 ${isPending ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                        &ldquo;{latest.response_text}&rdquo;
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {new Date(latest.submitted_at).toLocaleString()}
                      </div>
                      {priors.length > 0 && (
                        <details className="mt-1.5"
                                 data-testid={`driver-row-${k}-prior-history`}>
                          <summary className="text-[10px] text-slate-500 cursor-pointer">
                            Prior responses on this stage ({priors.length})
                          </summary>
                          <div className="mt-1 space-y-1 pl-2 border-l border-slate-200">
                            {priors.map((p, i) => (
                              <div key={p.submitted_at || `prior-${i}`} className="text-[11px] text-slate-600">
                                &ldquo;{p.response_text}&rdquo;
                                <span className="text-[10px] text-slate-400 ml-1">
                                  · {new Date(p.submitted_at).toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Stage</label>
            <select value={stage} onChange={(e) => setStage(e.target.value)}
                    data-testid="driver-stage-select"
                    className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5">
              {STAGE_KEYS.map((k) => <option key={k} value={k}>{STAGE_LABELS[k]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">State</label>
            <select value={state} onChange={(e) => setState(e.target.value)}
                    data-testid="driver-state-select"
                    className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5">
              {STATE_KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
        </div>

        {ACTION_STATES.has(state) && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">
              Action message (required, shown to founder)
            </label>
            <textarea value={actionMsg} onChange={(e) => setActionMsg(e.target.value)}
                       data-testid="driver-action-message"
                       rows={2}
                       className="w-full text-sm border border-amber-400 bg-amber-50 rounded-md px-2 py-1.5"
                       placeholder="The MCA asked for a different name — please pick a new one." />
          </div>
        )}

        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">
            Internal note (optional)
          </label>
          <input value={note} onChange={(e) => setNote(e.target.value)}
                 data-testid="driver-note"
                 className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"
                 placeholder="MCA SRN, internal reference, etc." />
        </div>

        <button onClick={submit} disabled={busy}
                data-testid="driver-submit"
                className="px-4 py-2 bg-[#C5283F] text-white text-sm font-semibold rounded-md hover:bg-[#a91d33] disabled:opacity-50">
          {busy ? "Saving…" : "Update filing progress"}
        </button>
      </div>
    </Card>
  );
}

// PDF / Zip export buttons. Native fetch — axios's XHR adapter with
// responseType:"blob" throws a synchronous DOMException from inside its
// onerror listener on any 4xx (it tries to read responseText which is
// illegal when responseType is "blob"). That sync throw bypasses the
// try/catch and surfaces as React's "Uncaught runtime error" overlay.
// fetch keeps the body opaque until we explicitly call .blob() / .json(),
// so success and error paths are clean and toast-only.
function CaseExportBtn({ caseId, kind }) {
  const [busy, setBusy] = useState(false);
  const isPdf = kind === "pdf";
  const path = isPdf
    ? `/backoffice/cases/${caseId}/pdf`
    : `/backoffice/cases/${caseId}/files.zip`;
  const filename = isPdf ? `${caseId}-case-summary.pdf` : `${caseId}-files.zip`;
  const click = async () => {
    setBusy(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${api.API}${path}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        // Map by status, do NOT rely on reading the response body — the
        // preview proxy occasionally consumes 4xx JSON bodies before our
        // code can read them, which made the previous detail-extraction
        // silently fall back to the generic message anyway.
        let detail;
        if (res.status === 404 && !isPdf) {
          detail = "No files to download";
        } else {
          detail = `Export failed (HTTP ${res.status})`;
        }
        toast.error(detail);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(isPdf ? "PDF exported" : "Files zip exported");
    } catch (e) {
      // Network / DNS / CORS — fetch never throws on HTTP status, only on these.
      toast.error("Export failed — please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={click} disabled={busy}
            data-testid={`export-${kind}-btn`}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-700 rounded-md disabled:opacity-50">
      {isPdf ? <FileDown size={12} /> : <Archive size={12} />}
      {busy ? "Preparing…" : (isPdf ? "PDF summary" : "Download all files")}
    </button>
  );
}



// WO 6c — pick the file_id this HIGH flag relates to. PAN-side flags
// go to pan_proof, Aadhaar/devanagari/address ones go to aadhaar_proof,
// photo flags to photo. Fallback: aadhaar_proof (the most common surface
// for HIGH flags). If the relevant doc isn't uploaded, returns null and
// the sign-off button is disabled.
function pickFileIdForFlag(documents, flagName) {
  const d = documents || {};
  const n = (flagName || "").toUpperCase();
  if (n.startsWith("PAN_") || n.includes("PAN_PORTRAIT")) {
    return d.pan_proof?.file_id || d.aadhaar_proof?.file_id || null;
  }
  if (n.includes("PHOTO") && !n.includes("AADHAAR")) {
    return d.photo?.file_id || d.pan_proof?.file_id || null;
  }
  // Default: Aadhaar (where the bulk of HIGH flags surface).
  return d.aadhaar_proof?.file_id || d.pan_proof?.file_id || d.photo?.file_id || null;
}


// WO 6c — per-flag admin sign-off button. Opens a typed-reason dialog
// (min 8 chars, mirroring the founder authenticity-override surface),
// posts to /backoffice/cases/{id}/manual-verification, and the case
// detail page refreshes to show the signed-off chip + audit metadata.
function ManualVerifyButton({ caseId, stakeholderId, flagName,
                                fileIdHint, onSigned }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const disabled = !fileIdHint;

  async function submit() {
    if (reason.trim().length < 8) {
      toast.error("Please type a reason (min 8 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.backofficeRecordManualVerification(
        caseId, stakeholderId, fileIdHint, flagName, reason.trim());
      setOpen(false);
      setReason("");
      await onSigned?.();
    } catch (e) {
      const msg = e?.response?.data?.detail
                || "Could not record sign-off";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid={`bo-verify-btn-${stakeholderId}-${flagName}`}
        title={disabled
          ? "Cannot sign off — the relevant document is not uploaded."
          : "Record a per-flag manual verification (audit trail)."}
        className="shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border border-emerald-700 text-emerald-700 bg-white hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Mark as manually verified
      </button>
    );
  }
  return (
    <div className="shrink-0 w-72 bg-white border border-emerald-300 rounded-lg p-3 shadow-md"
          data-testid={`bo-verify-dialog-${stakeholderId}-${flagName}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-800 mb-1.5">
        Sign off — {flagName}
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Type your reason (min 8 chars). The CA's name and timestamp are logged automatically."
        data-testid={`bo-verify-reason-${stakeholderId}-${flagName}`}
        className="w-full text-[12px] border border-slate-300 rounded-md px-2 py-1.5 mb-2 font-sans"
      />
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => { setOpen(false); setReason(""); }}
          data-testid={`bo-verify-cancel-${stakeholderId}-${flagName}`}
          className="text-[11px] text-slate-600 hover:text-slate-900 px-2 py-1">
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={busy || reason.trim().length < 8}
          data-testid={`bo-verify-submit-${stakeholderId}-${flagName}`}
          className="text-[11px] bg-emerald-700 text-white px-3 py-1 rounded-md hover:bg-emerald-800 disabled:opacity-50">
          {busy ? "Signing…" : "Confirm sign-off"}
        </button>
      </div>
    </div>
  );
}
